// src/pages/Home.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortfolioTables from '../components/PortfolioTables';
import SummaryWidget from '../components/DashboardSummary';
import KiteBanner from '../components/KiteBanner';
import { authFetch } from '../utils/api';

// ── Recommendation badge styles ─────────────────────────────────────────────

const REC_COLORS = {
  'ADD MORE': { bg: '#dcfce7', color: '#15803d' },
  'HOLD':     { bg: '#fef9c3', color: '#854d0e' },
  'TRIM':     { bg: '#ffedd5', color: '#9a3412' },
  'EXIT':     { bg: '#fee2e2', color: '#991b1b' },
};

// ── Analysis panel (shared between mobile and desktop) ──────────────────────

function AnalysisPanel({ data }) {
  if (!data) return null;
  const rec = data.recommendation;
  const rs = REC_COLORS[rec] || { bg: '#334155', color: '#e2e8f0' };
  const bd = data.score_breakdown || {};
  const tech = bd.tech || {};
  const f = data.fundamentals;

  const fmt = (v, dec = 1) => v != null ? Number(v).toFixed(dec) : '—';

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '0.65rem 0.8rem', margin: '0.25rem 0 0.5rem',
    }}>
      {/* Row 1: Recommendation + Scores */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.45rem', flexWrap: 'wrap' }}>
        <span style={{
          background: rs.bg, color: rs.color,
          padding: '0.15rem 0.5rem', borderRadius: 5,
          fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em',
        }}>{rec || '—'}</span>
        <span style={{ color: '#0f172a', fontSize: '0.76rem', fontWeight: 600 }}>
          Tech {data.tech_score ?? '—'}/7
        </span>
        <span style={{ color: '#0f172a', fontSize: '0.76rem', fontWeight: 600 }}>
          Fund {data.fund_score ?? '—'}/9
        </span>
        <span style={{ color: '#0369a1', fontSize: '0.76rem', fontWeight: 700 }}>
          Total {data.total_score ?? '—'}/16
        </span>
      </div>

      {/* Row 2: Key technical indicators */}
      <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', fontSize: '0.72rem', color: '#64748b' }}>
        {data.rsi != null && (
          <span>RSI <strong style={{ color: '#0f172a' }}>{fmt(data.rsi)}</strong></span>
        )}
        {tech.macd_bullish != null && (
          <span>MACD <strong style={{ color: tech.macd_bullish ? '#16a34a' : '#dc2626' }}>
            {tech.macd_bullish ? 'Bull' : 'Bear'}</strong></span>
        )}
        {tech.price_gt_ema50 != null && (
          <span>&gt;EMA50 <strong style={{ color: tech.price_gt_ema50 ? '#16a34a' : '#dc2626' }}>
            {tech.price_gt_ema50 ? 'Yes' : 'No'}</strong></span>
        )}
        {tech.price_gt_ema200 != null && (
          <span>&gt;EMA200 <strong style={{ color: tech.price_gt_ema200 ? '#16a34a' : '#dc2626' }}>
            {tech.price_gt_ema200 ? 'Yes' : 'No'}</strong></span>
        )}
        {tech.volume_breakout != null && tech.volume_breakout && (
          <span style={{ color: '#38bdf8', fontWeight: 600 }}>Vol Breakout</span>
        )}
      </div>

      {/* Row 3: Fundamentals summary */}
      {f && (
        <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.85rem', flexWrap: 'wrap', fontSize: '0.72rem', color: '#64748b' }}>
          {f.roe != null && <span>ROE <strong style={{ color: '#0f172a' }}>{fmt(f.roe)}%</strong></span>}
          {f.debt_equity != null && <span>D/E <strong style={{ color: '#0f172a' }}>{fmt(f.debt_equity)}</strong></span>}
          {f.profit_growth != null && <span>Profit G <strong style={{ color: '#0f172a' }}>{fmt(f.profit_growth)}%</strong></span>}
          {f.pe_ratio != null && <span>P/E <strong style={{ color: '#0f172a' }}>{fmt(f.pe_ratio)}</strong></span>}
          {f.promoter_holding != null && <span>Promoter <strong style={{ color: '#0f172a' }}>{fmt(f.promoter_holding)}%</strong></span>}
        </div>
      )}

      {/* Analyzed timestamp */}
      {data.analyzed_at && (
        <div style={{ marginTop: '0.3rem', fontSize: '0.65rem', color: '#94a3b8' }}>
          Analyzed: {new Date(data.analyzed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      )}
    </div>
  );
}

// ── AI Action badge + conviction stars ──────────────────────────────────────

const ACTION_COLORS = {
  ACCUMULATE_NOW:  { bg: '#dcfce7', color: '#15803d' },
  BUY_ON_DIPS:     { bg: '#d1fae5', color: '#065f46' },
  FRESH_BUY:       { bg: '#d1fae5', color: '#065f46' },
  HOLD_STRONG:     { bg: '#fef9c3', color: '#854d0e' },
  HOLD_CAUTIOUS:   { bg: '#ffedd5', color: '#9a3412' },
  REDUCE_ON_RALLY: { bg: '#fee2e2', color: '#991b1b' },
  AVOID_ADDING:    { bg: '#fecaca', color: '#7f1d1d' },
};

function ActionBadge({ action }) {
  const s = ACTION_COLORS[action] || { bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '0.12rem 0.45rem', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700 }}>
      {(action || '—').replace(/_/g, ' ')}
    </span>
  );
}

function Stars({ n }) {
  return <span style={{ fontSize: '0.65rem', letterSpacing: '1px', color: '#f59e0b' }}>{'★'.repeat(n || 0)}{'☆'.repeat(5 - (n || 0))}</span>;
}

// ── AI Insight panel (per-holding) ──────────────────────────────────────────

function AiInsightPanel({ data }) {
  if (!data) return null;
  const pl = data.price_levels || {};
  const fc = data.if_deploying_fresh_capital || {};
  const an = data.analysis || {};
  const fmtP = v => v != null ? '₹' + Number(v).toLocaleString('en-IN') : '—';

  return (
    <div style={{
      background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8,
      padding: '0.6rem 0.8rem', margin: '0.15rem 0 0.4rem',
    }}>
      {/* Action + conviction + thesis */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
        <ActionBadge action={data.action} />
        <Stars n={data.conviction} />
        {an.thesis_broken && (
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 3, padding: '0 4px' }}>THESIS BROKEN</span>
        )}
      </div>

      {/* Price levels as pills */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
        {pl.ideal_buy_range_low != null && (
          <span style={{ fontSize: '0.68rem', background: '#e0f2fe', color: '#0369a1', padding: '0.1rem 0.4rem', borderRadius: 4, fontWeight: 600 }}>
            Buy {fmtP(pl.ideal_buy_range_low)}–{fmtP(pl.ideal_buy_range_high)}
          </span>
        )}
        {pl.stop_loss != null && (
          <span style={{ fontSize: '0.68rem', background: '#fee2e2', color: '#991b1b', padding: '0.1rem 0.4rem', borderRadius: 4, fontWeight: 600 }}>
            SL {fmtP(pl.stop_loss)}
          </span>
        )}
        {pl.target_6m != null && (
          <span style={{ fontSize: '0.68rem', background: '#f3e8ff', color: '#6b21a8', padding: '0.1rem 0.4rem', borderRadius: 4, fontWeight: 600 }}>
            6m {fmtP(pl.target_6m)}
          </span>
        )}
        {pl.target_12m != null && (
          <span style={{ fontSize: '0.68rem', background: '#f3e8ff', color: '#6b21a8', padding: '0.1rem 0.4rem', borderRadius: 4, fontWeight: 600 }}>
            12m {fmtP(pl.target_12m)}
          </span>
        )}
      </div>

      {/* Fresh capital deployment */}
      {fc.suggested_amount > 0 && (
        <div style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 600, marginBottom: '0.3rem' }}>
          Deploy {fmtP(fc.suggested_amount)}
          {fc.tranche && <span style={{ color: '#64748b', fontWeight: 400 }}> · {fc.tranche.replace(/_/g, ' ')}</span>}
          {fc.deploy_condition && <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 400 }}>{fc.deploy_condition}</div>}
        </div>
      )}

      {/* Summaries — 2-column on desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem', fontSize: '0.7rem' }}>
        {an.technical_summary && (
          <div><span style={{ color: '#0369a1', fontWeight: 700, fontSize: '0.63rem', textTransform: 'uppercase' }}>Technical</span>
          <p style={{ color: '#475569', margin: '2px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{an.technical_summary}</p></div>
        )}
        {an.fundamental_summary && (
          <div><span style={{ color: '#7c3aed', fontWeight: 700, fontSize: '0.63rem', textTransform: 'uppercase' }}>Fundamental</span>
          <p style={{ color: '#475569', margin: '2px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{an.fundamental_summary}</p></div>
        )}
      </div>

      {/* Bull/Bear */}
      {(an.bull_case || an.bear_case) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginTop: '0.3rem', fontSize: '0.67rem', fontStyle: 'italic' }}>
          {an.bull_case && <div style={{ color: '#16a34a' }}>Bull: {an.bull_case}</div>}
          {an.bear_case && <div style={{ color: '#dc2626' }}>Bear: {an.bear_case}</div>}
        </div>
      )}
    </div>
  );
}

// ── AI Summary Bar (collapsible, below portfolio summary) ───────────────────

const HEALTH_COLOR = { STRONG: '#16a34a', FAIR: '#f59e0b', WEAK: '#f97316', CRITICAL: '#dc2626' };
const PHASE_COLOR  = { BULL: '#16a34a', CORRECTION: '#f59e0b', BEAR: '#dc2626', SIDEWAYS: '#64748b' };

function AiSummaryBar({ meta, open, onToggle }) {
  if (!meta) return null;
  const ps = meta.portfolio_summary || {};
  const am = meta.analysis_metadata || {};
  const dp = meta.deployment_plan || {};
  const fmtA = v => v != null ? '₹' + Number(v).toLocaleString('en-IN') : '';

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      {/* Collapsed header — always visible */}
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.85rem', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed' }}>AI Insights</span>
        {ps.overall_portfolio_health && (
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: HEALTH_COLOR[ps.overall_portfolio_health] || '#94a3b8' }}>
            {ps.overall_portfolio_health}
          </span>
        )}
        {am.market_phase && (
          <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            Market: <strong style={{ color: PHASE_COLOR[am.market_phase] || '#94a3b8' }}>{am.market_phase}</strong>
          </span>
        )}
        {ps.top_opportunity && (
          <span style={{ fontSize: '0.68rem', color: '#16a34a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ps.top_opportunity}
          </span>
        )}
        {meta.analyzed_at && (
          <span style={{ fontSize: '0.63rem', color: '#94a3b8', marginLeft: 'auto' }}>
            {new Date(meta.analyzed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        )}
        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Expanded content */}
      {open && (
        <div style={{ padding: '0 0.85rem 0.65rem', borderTop: '1px solid #f1f5f9' }}>
          {/* Market context pills */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            {[
              am.market_phase && { label: 'Market', value: am.market_phase, color: PHASE_COLOR[am.market_phase] },
              am.geopolitical_risk_level && { label: 'Geo Risk', value: am.geopolitical_risk_level, color: am.geopolitical_risk_level === 'HIGH' || am.geopolitical_risk_level === 'EXTREME' ? '#dc2626' : '#f59e0b' },
              am.nifty_at_analysis > 0 && { label: 'Nifty', value: Number(am.nifty_at_analysis).toLocaleString('en-IN'), color: '#0369a1' },
              am.india_vix > 0 && { label: 'VIX', value: Number(am.india_vix).toFixed(1), color: am.india_vix > 20 ? '#dc2626' : '#0369a1' },
              am.brent_crude_usd > 0 && { label: 'Brent', value: `$${Number(am.brent_crude_usd).toFixed(0)}`, color: '#b45309' },
              am.usdinr > 0 && { label: 'USD/INR', value: Number(am.usdinr).toFixed(1), color: '#0f172a' },
            ].filter(Boolean).map((p, i) => (
              <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.3rem 0.55rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.58rem', color: '#64748b', textTransform: 'uppercase' }}>{p.label}</div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: p.color }}>{p.value}</div>
              </div>
            ))}
          </div>

          {/* Portfolio health + risk/opportunity */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.72rem', marginBottom: '0.4rem' }}>
            {ps.top_risk && <div style={{ color: '#dc2626' }}>Risk: {ps.top_risk}</div>}
            {ps.top_opportunity && <div style={{ color: '#16a34a' }}>Opportunity: {ps.top_opportunity}</div>}
            {meta.liquid_amount > 0 && <div style={{ color: '#64748b' }}>Liquid: {fmtA(meta.liquid_amount)}</div>}
          </div>

          {/* Deployment plan summary */}
          {dp.tranches?.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.68rem', color: '#64748b' }}>Deploy {fmtA(dp.total_to_deploy)} · Buffer {fmtA(dp.buffer_to_retain)}</span>
              {dp.tranches.map(t => (
                <span key={t.tranche_id} style={{ fontSize: '0.66rem', background: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: 4, padding: '0.1rem 0.4rem', color: '#6b21a8', fontWeight: 600 }}>
                  {t.tranche_id} {fmtA(t.amount)}
                </span>
              ))}
            </div>
          )}

          {/* Geopolitical summary */}
          {am.geopolitical_summary && (
            <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.35rem', fontStyle: 'italic' }}>
              {am.geopolitical_summary}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Fresh Opportunity Card ──────────────────────────────────────────────────

function FreshOpportunityCard({ o }) {
  const pl = o.price_levels || {};
  const fmtP = v => v != null ? '₹' + Number(v).toLocaleString('en-IN') : '—';
  return (
    <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '0.7rem 0.85rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{o.symbol}</span>
          {o.sector && <span style={{ fontSize: '0.63rem', color: '#94a3b8', marginLeft: 6 }}>{o.sector}</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <ActionBadge action="FRESH_BUY" />
          <Stars n={o.conviction} />
        </div>
      </div>
      {o.why_now && <p style={{ fontSize: '0.72rem', color: '#4c1d95', margin: '0 0 5px', lineHeight: 1.4, fontStyle: 'italic' }}>{o.why_now}</p>}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', fontSize: '0.68rem' }}>
        {pl.current_price > 0 && <span style={{ background: '#f1f5f9', color: '#0f172a', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>CMP {fmtP(pl.current_price)}</span>}
        {pl.ideal_entry_low > 0 && <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>Entry {fmtP(pl.ideal_entry_low)}–{fmtP(pl.ideal_entry_high)}</span>}
        {pl.stop_loss > 0 && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>SL {fmtP(pl.stop_loss)}</span>}
        {pl.target_12m > 0 && <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>12m {fmtP(pl.target_12m)}</span>}
      </div>
      {o.suggested_amount > 0 && (
        <div style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 600, color: '#7c3aed' }}>
          {fmtP(o.suggested_amount)} · {(o.tranche || '').replace(/_/g, ' ')}
        </div>
      )}
    </div>
  );
}

// ── Kite-style mobile holding/position card ─────────────────────────────────

function HoldingCard({ row, analysis, expanded, onToggle, aiAnalysis }) {
  const pnl    = Number(row.pnl || 0);
  const dayPct = Number(row.day_change_percentage || 0);
  const dayChg = Number(row.day_change || 0);
  const qty    = Number(row.quantity || 0);
  const avg    = Number(row.average_price || 0);
  const ltp    = Number(row.last_price || 0);
  const invested = qty * avg;
  const current  = qty * ltp;
  const totalPct = invested > 0 ? ((current - invested) / invested) * 100 : 0;

  const pnlColor  = pnl  >= 0 ? '#1db954' : '#e84040';
  const dayColor  = dayPct >= 0 ? '#1db954' : '#e84040';

  const fmt = (n, dec = 2) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

  return (
    <div>
      <div
        style={{ ...cardStyles.card, cursor: (analysis || aiAnalysis) ? 'pointer' : 'default' }}
        onClick={(analysis || aiAnalysis) ? onToggle : undefined}
      >
        <div style={cardStyles.row}>
          {/* Left: symbol + qty info */}
          <div style={cardStyles.left}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span style={cardStyles.symbol}>{row.tradingsymbol}</span>
              {analysis && (
                <span style={{
                  background: (REC_COLORS[analysis.recommendation] || {}).bg || '#334155',
                  color: (REC_COLORS[analysis.recommendation] || {}).color || '#e2e8f0',
                  padding: '0.05rem 0.35rem', borderRadius: 4,
                  fontSize: '0.6rem', fontWeight: 700,
                }}>{analysis.recommendation}</span>
              )}
              {aiAnalysis && <ActionBadge action={aiAnalysis.action} />}
            </div>
            <span style={cardStyles.meta}>
              {qty} qty · Avg ₹{fmt(avg)}
            </span>
          </div>

          {/* Right: LTP + day change */}
          <div style={cardStyles.right}>
            <span style={cardStyles.ltp}>₹{fmt(ltp)}</span>
            <span style={{ ...cardStyles.pnl, color: dayColor }}>
              {dayChg >= 0 ? '+' : ''}₹{fmt(Math.abs(dayChg * qty))}&nbsp;
              <span style={cardStyles.pct}>({dayPct >= 0 ? '+' : ''}{fmt(dayPct)}%)</span>
            </span>
          </div>
        </div>

        {/* Bottom bar: invested → current */}
        <div style={cardStyles.subRow}>
          <span style={cardStyles.subItem}>
            Invested <strong>₹{fmt(invested)}</strong>
          </span>
          <span style={cardStyles.subItem}>
            Current <strong style={{ color: pnlColor }}>₹{fmt(current)}</strong>
            <span style={{ color: pnlColor, fontSize: '0.65rem', marginLeft: 3 }}>
              ({totalPct >= 0 ? '+' : ''}{fmt(totalPct)}%)
            </span>
          </span>
          {(analysis || aiAnalysis) && (
            <span style={{ fontSize: '0.65rem', color: '#38bdf8' }}>
              {expanded ? '▾' : '▸'}
            </span>
          )}
        </div>
      </div>
      {expanded && analysis && <AnalysisPanel data={analysis} />}
      {expanded && aiAnalysis && <AiInsightPanel data={aiAnalysis} />}
    </div>
  );
}

function PositionCard({ row }) {
  const qty      = Number(row.quantity || 0);
  const avg      = Number(row.average_price || row.average_price || 0);
  const ltp      = Number(row.last_price || 0);
  const pnl      = Number(row.pnl || 0);
  const product  = row.product || '';

  const pnlColor = pnl >= 0 ? '#1db954' : '#e84040';
  const fmt = (n, dec = 2) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

  return (
    <div style={{ ...cardStyles.card, borderLeft: `3px solid ${qty >= 0 ? '#1db954' : '#e84040'}` }}>
      <div style={cardStyles.row}>
        <div style={cardStyles.left}>
          <span style={cardStyles.symbol}>{row.tradingsymbol}</span>
          <span style={cardStyles.meta}>
            {qty >= 0 ? 'LONG' : 'SHORT'} {Math.abs(qty)} · {product}
          </span>
        </div>
        <div style={cardStyles.right}>
          <span style={cardStyles.ltp}>₹{fmt(ltp)}</span>
          <span style={{ ...cardStyles.pnl, color: pnlColor }}>
            {pnl >= 0 ? '+' : ''}₹{fmt(pnl)}
          </span>
        </div>
      </div>
      {avg > 0 && (
        <div style={cardStyles.subRow}>
          <span style={cardStyles.subItem}>Avg ₹{fmt(avg)}</span>
          <span style={cardStyles.subItem}>LTP ₹{fmt(ltp)}</span>
        </div>
      )}
    </div>
  );
}

const cardStyles = {
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  right: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
    flexShrink: 0,
    marginLeft: '0.5rem',
  },
  symbol: {
    fontWeight: 700,
    fontSize: '0.97rem',
    color: '#1e293b',
    letterSpacing: '-0.01em',
  },
  meta: {
    fontSize: '0.75rem',
    color: '#94a3b8',
  },
  ltp: {
    fontWeight: 700,
    fontSize: '0.97rem',
    color: '#1e293b',
  },
  pnl: {
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  pct: {
    fontWeight: 400,
  },
  subRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.45rem',
    paddingTop: '0.4rem',
    borderTop: '1px solid #f1f5f9',
  },
  subItem: {
    fontSize: '0.72rem',
    color: '#64748b',
  },
};

// ── Section header with count badge ─────────────────────────────────────────

function SectionHeader({ title, count }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
    }}>
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>
        {title}
      </h2>
      {count > 0 && (
        <span style={{
          background: '#e0f2fe', color: '#0369a1',
          fontSize: '0.7rem', fontWeight: 700,
          padding: '0.1rem 0.45rem', borderRadius: 10,
        }}>
          {count}
        </span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Home() {
  const [holdings, setHoldings]   = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);    // fatal error (no data at all)
  const [staleBanner, setStaleBanner] = useState(null); // non-fatal: stale data warning
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // Analysis state
  const [analysisMap, setAnalysisMap]       = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [expandedRows, setExpandedRows]     = useState(new Set());

  // AI analysis state
  const [aiMap, setAiMap]               = useState(null);
  const [aiMeta, setAiMeta]             = useState(null);
  const [aiLoading, setAiLoading]       = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchPortfolio = () => {
    setLoading(true);
    setError(null);
    setStaleBanner(null);

    authFetch('/api/portfolio/sync')
      .then(async (res) => {
        if (res.status === 401) {
          const d = await res.json();
          throw Object.assign(new Error(d.detail || 'Token expired'), { isToken: true });
        }
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(({ holdings: hData, positions: pData, stale, stale_reason }) => {
        const hList = Array.isArray(hData)
          ? hData
          : hData && typeof hData === 'object' ? Object.values(hData) : [];
        setHoldings(hList);
        const day = pData && Array.isArray(pData.day) ? pData.day : [];
        const net = pData && Array.isArray(pData.net) ? pData.net : [];
        // Use net positions (complete picture incl. carry-forward).
        // Fall back to day positions if net is empty (pure intraday session).
        setPositions(net.length > 0 ? net : day);

        if (stale) setStaleBanner(stale_reason || 'Showing cached data — Zerodha API unavailable.');
      })
      .catch((err) => {
        console.error(err);
        setError({ msg: err.message, isToken: !!err.isToken });
      })
      .finally(() => setLoading(false));
  };

  const fetchAnalysis = useCallback(() => {
    setAnalysisLoading(true);
    authFetch('/api/portfolio/analysis/cached')
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        (data.results || []).forEach(r => { map[r.symbol] = r; });
        if (Object.keys(map).length > 0) setAnalysisMap(map);
      })
      .catch(err => console.error('Analysis fetch failed:', err))
      .finally(() => setAnalysisLoading(false));
  }, []);

  const hideAnalysis = () => {
    setAnalysisMap(null);
    setExpandedRows(new Set());
  };

  const fetchAiInsights = useCallback(() => {
    setAiLoading(true);
    authFetch('/api/portfolio/ai-analysis/latest')
      .then(async res => {
        if (!res.ok) return;
        const data = await res.json();
        const r = data.result || {};
        const map = {};
        (r.holdings_analysis || []).forEach(h => { map[h.symbol] = h; });
        if (Object.keys(map).length > 0) {
          setAiMap(map);
          setAiMeta({
            fresh_opportunities: r.fresh_opportunities || [],
            deployment_plan: r.deployment_plan || null,
            portfolio_summary: r.portfolio_summary || null,
            analysis_metadata: r.analysis_metadata || null,
            analyzed_at: data.analyzed_at,
            liquid_amount: data.liquid_amount,
          });
          setAiSummaryOpen(true);
        }
      })
      .catch(err => console.error('AI insights fetch failed:', err))
      .finally(() => setAiLoading(false));
  }, []);

  const hideAiInsights = () => {
    setAiMap(null); setAiMeta(null); setAiSummaryOpen(false);
  };

  const handleBuyClick = useCallback((symbol, aiHolding) => {
    const pl = aiHolding?.price_levels || {};
    const fc = aiHolding?.if_deploying_fresh_capital || {};
    const buyPrice = pl.ideal_buy_range_high || pl.ideal_buy_range_low || '';
    const qty = buyPrice && fc.suggested_amount ? Math.floor(fc.suggested_amount / buyPrice) : '';
    const params = new URLSearchParams({
      symbol,
      transaction_type: 'BUY',
      order_type: buyPrice ? 'LIMIT' : 'MARKET',
      product: 'CNC',
      ...(qty && { quantity: String(qty) }),
      ...(buyPrice && { price: String(Number(buyPrice).toFixed(2)) }),
    });
    navigate(`/orders?${params.toString()}`);
  }, [navigate]);

  const toggleExpand = useCallback((symbol) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(symbol) ? next.delete(symbol) : next.add(symbol);
      return next;
    });
  }, []);

  useEffect(() => { fetchPortfolio(); }, []);

  if (loading) return <p style={{ padding: '1rem' }}>Loading portfolio…</p>;

  if (error) {
    const isToken    = error.isToken;
    const isSetup    = !isToken && (
      error.msg.toLowerCase().includes('not configured') ||
      error.msg.toLowerCase().includes('credentials')
    );
    return (
      <div style={{ padding: '1rem' }}>
        <div style={{
          padding: '1rem', borderRadius: 8, marginBottom: '1rem',
          backgroundColor: '#fff3e0', border: '1px solid #ffcc02',
        }}>
          <strong>{isToken ? 'Access token expired' : 'Could not load portfolio'}</strong>
          <p style={{ margin: '0.4rem 0 0.75rem', fontSize: '0.9rem', color: '#555' }}>
            {error.msg}
          </p>
          {isToken && (
            <button onClick={() => navigate('/settings')}
              style={{ padding: '0.4rem 1rem', backgroundColor: '#1976d2', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, marginRight: '0.5rem' }}>
              Go to Settings → Refresh Token
            </button>
          )}
          {isSetup && (
            <Link to="/settings" style={{ fontWeight: 600 }}>Set up credentials in Settings →</Link>
          )}
          <button onClick={fetchPortfolio}
            style={{ padding: '0.4rem 1rem', backgroundColor: 'transparent', color: '#1976d2',
              border: '1px solid #1976d2', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const analysisBtnStyle = {
    padding: '0.25rem 0.7rem',
    border: '1px solid #38bdf8',
    borderRadius: 5,
    color: '#38bdf8',
    background: analysisMap ? '#0c4a6e' : 'transparent',
    cursor: analysisLoading ? 'not-allowed' : 'pointer',
    fontSize: '0.75rem',
    fontWeight: 600,
  };

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1rem' }}>

      <KiteBanner message="Zerodha Kite not set up — portfolio sync requires Kite credentials." />

      {/* Stale data warning banner */}
      {staleBanner && (
        <div style={{
          padding: '0.6rem 1rem', marginBottom: '0.75rem', borderRadius: 6,
          backgroundColor: '#fff8e1', border: '1px solid #ffe082',
          fontSize: '0.85rem', color: '#795548',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>⚠️ {staleBanner}</span>
          <button onClick={fetchPortfolio}
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem', border: '1px solid #c8a000',
              borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#795548' }}>
            Retry Live
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 700, color: '#e2e8f0' }}>
          My Portfolio
        </h1>
        <button
          onClick={fetchPortfolio}
          style={{
            padding: '0.35rem 0.9rem',
            border: '1px solid #0ea5e9',
            borderRadius: 6,
            color: '#0ea5e9',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '0.82rem',
            fontWeight: 600,
          }}
        >
          Refresh
        </button>
      </div>

      {/* Summary widget */}
      <SummaryWidget rows={holdings} />

      {/* AI Summary bar (below portfolio metrics) */}
      {aiMeta && (
        <AiSummaryBar meta={aiMeta} open={aiSummaryOpen} onToggle={() => setAiSummaryOpen(o => !o)} />
      )}

      {/* Holdings */}
      {holdings.length > 0 ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
            <SectionHeader title="Holdings" count={holdings.length} />
            <button
              onClick={analysisMap ? hideAnalysis : fetchAnalysis}
              disabled={analysisLoading}
              style={analysisBtnStyle}
            >
              {analysisLoading ? 'Loading…' : analysisMap ? 'Hide Analysis' : 'Show Analysis'}
            </button>
            <button
              onClick={aiMap ? hideAiInsights : fetchAiInsights}
              disabled={aiLoading}
              style={{
                padding: '0.25rem 0.7rem', borderRadius: 5, fontSize: '0.75rem', fontWeight: 600,
                border: '1px solid #a78bfa', color: '#a78bfa',
                background: aiMap ? '#2e1065' : 'transparent',
                cursor: aiLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {aiLoading ? 'Loading…' : aiMap ? 'Hide AI Insights' : 'AI Insights'}
            </button>
          </div>
          {isMobile ? (
            <div>
              {holdings.map((row, i) => {
                const sym = row.tradingsymbol || row.symbol;
                return (
                  <HoldingCard
                    key={row.instrument_token || sym || i}
                    row={row}
                    analysis={analysisMap?.[sym]}
                    aiAnalysis={aiMap?.[sym]}
                    expanded={expandedRows.has(sym)}
                    onToggle={() => toggleExpand(sym)}
                  />
                );
              })}
            </div>
          ) : (
            <PortfolioTables
              rows={holdings}
              analysisData={analysisMap}
              aiData={aiMap}
              expandedRows={expandedRows}
              onToggleExpand={toggleExpand}
              onBuyClick={handleBuyClick}
            />
          )}
        </>
      ) : (
        <p style={{ color: '#888', marginTop: '1rem' }}>No holdings to show.</p>
      )}

      {/* Positions */}
      {positions.length > 0 ? (
        <>
          <SectionHeader title="Positions" count={positions.length} />
          {isMobile ? (
            <div>
              {positions.map((row, i) => (
                <PositionCard key={row.instrument_token || row.tradingsymbol || i} row={row} />
              ))}
            </div>
          ) : (
            <PortfolioTables rows={positions} type="positions" />
          )}
        </>
      ) : (
        <p style={{ color: '#888', marginTop: '1rem' }}>No open positions.</p>
      )}

      {/* Fresh Opportunities (from AI analysis) */}
      {aiMeta?.fresh_opportunities?.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <SectionHeader title="New Opportunities" count={aiMeta.fresh_opportunities.length} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.6rem' }}>
            {aiMeta.fresh_opportunities.map((o, i) => <FreshOpportunityCard key={i} o={o} />)}
          </div>
        </div>
      )}
    </div>
  );
}
